import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import BlurhashImg from '../../@/components/Image/BlurhashImg';
import './learn.css';

const categories = [
  { id: 1, name: "Architectural", value: "Architecture" },
  { id: 2, name: "Industrial", value: "Industrial" },
  { id: 3, name: "Graphics", value: "Graphics" },
  { id: 4, name: "Furniture", value: "Furniture" }
];

const Learn = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [visibleCount, setVisibleCount] = useState(9);
  const [visibleLastSectionCount, setVisibleLastSectionCount] = useState(3);
  const [visibleRemainingCount, setVisibleRemainingCount] = useState(3);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectsCollection = collection(db, 'projects');
        const snapshot = await getDocs(projectsCollection);
        
        const fetchedProjects = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            isVisible: data.isVisible !== undefined ? data.isVisible : true
          };
        });

        fetchedProjects.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return dateB - dateA;
        });

        setProjects(fetchedProjects);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setError("Failed to load projects. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const normalizeCategory = (category) => {
    const categoryMap = {
      'architectural': 'Architecture',
      'industrial': 'Industrial',
      'graphics': 'Graphics',
      'furniture': 'Furniture'
    };
    return categoryMap[category?.toLowerCase()] || category || '';
  };

  const filteredProjects = projects.filter((project) => {
    const projectCategory = normalizeCategory(project.category);
    const selectedCategoryValue = categories.find(cat => cat.value === selectedCategory)?.name || selectedCategory;

    return project.isVisible && (
      selectedCategory === 'all' || 
      normalizeCategory(projectCategory) === normalizeCategory(selectedCategoryValue)
    );
  });

  const loadMore = () => {
    setVisibleCount(prevCount => prevCount + 9);
  };

  const getProjectLink = (project) => {
    return project.slug ? `/work/${project.slug}` : `/work/${project.id}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
        <p className="ml-4 text-lg">Loading Case Studies...</p>
      </div>
    );
  }
  

  if (filteredProjects.length === 0) {
    return (
      <div className="mx-auto section__paddingda">
        <div className="category-buttons flex flex-wrap items-center justify-start mb-2">
          <button
            key="all"
            onClick={() => setSelectedCategory('all')}
            className={`category-button ${selectedCategory === 'all' ? 'active' : ''}`}
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.value)}
              className={`category-button ${selectedCategory === category.value ? 'active' : ''}`}
            >
              {category.name}
            </button>
          ))}
        </div>
        <div className="text-center pt-12 pb-0">
          <h2 className="text-xl font-semibold mb-2">No projects found</h2>
          <p>There are currently no projects in the {selectedCategory} category.</p>
        </div>
      </div>
    );
  }

  // const visibleProjects = filteredProjects.slice(0, visibleCount);
  const visibleProjects = filteredProjects;
  const featuredProject = visibleProjects[0];
  const remainingProjects = visibleProjects.slice(1);


  return (
    <div className="mx-auto section__paddingda">
      {/* Category Buttons */}
      <div className="category-buttons-learn">
      {["all", ...categories.map((cat) => cat.value)].map((category) => (
        <button
          key={category}
          onClick={() => {
            setSelectedCategory(category);
            setVisibleLastSectionCount(3);
          }}
          className={`category-button ${selectedCategory === category ? 'active' : ''}`}
        >
          {category === "all" ? "All" : categories.find((cat) => cat.value === category)?.name}
        </button>
      ))}
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Featured Project */}
      <div className="md:col-span-2 lg:col-span-2 lg:row-span-2 aspect-[4/3] w-full">
        {featuredProject && (
          <Link to={getProjectLink(featuredProject)} className="block h-full">
            <div className="relative h-full group">
              <div className="absolute inset-0 overflow-hidden">
                <BlurhashImg
                  src={featuredProject.coverImage}
                  placeholderColor="#888"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-white bg-opacity-90">
                <h2 className="text-black text-lg font-semibold truncate">
                  {featuredProject.title}
                </h2>
              </div>
            </div>
          </Link>
        )}
      </div>

      {/* Grid of Projects */}
      {visibleProjects.slice(1, 5).map((project, index) => (
        <div 
          key={project.id} 
          className={`aspect-[4/3] w-full ${
            index === 0 ? 'md:col-start-3' : 
            index === 1 ? 'md:col-start-4' : 
            index === 2 ? 'md:col-start-3 md:row-start-2' : 
            'md:col-start-4 md:row-start-2'
          }`}
        >
          <Link to={getProjectLink(project)} className="block h-full">
            <div className="relative h-full">
              <div className="absolute inset-0 overflow-hidden">
                <BlurhashImg
                  src={project.coverImage}
                  placeholderColor="#888"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-white bg-opacity-90">
                <h2 className="text-black text-lg font-semibold truncate">
                  {project.title}
                </h2>
              </div>
            </div>
          </Link>
        </div>
      ))}
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
      {/* Featured Project */}
      <div className="md:col-span-2 lg:col-span-2 lg:col-start-3 lg:row-span-2 h-[290px] md:h-[600px]">
        {remainingProjects[4] && (
          <Link to={getProjectLink(remainingProjects[4])} className="block h-full w-full">
            <div className="relative w-full h-full group">
              <div className="absolute inset-0 overflow-hidden">
                <BlurhashImg
                  src={remainingProjects[4].coverImage}
                  placeholderColor="#888"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/fallback-image.jpg';
                  }}
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-white bg-opacity-90">
                <h2 className="text-black text-lg font-semibold truncate">
                  {remainingProjects[4].title}
                </h2>
              </div>
            </div>
          </Link>
        )}
      </div>

      {/* Smaller Projects */}
      {remainingProjects.slice(5, 7).map((project, index) => (
        <div
          key={project.id}
          className={`md:col-span-2 h-[290px] ${
            index === 0
              ? 'lg:col-start-1 lg:row-start-1'
              : 'lg:col-start-1 lg:row-start-2'
          }`}
        >
          <Link to={getProjectLink(project)} className="block h-full w-full">
            <div className="relative w-full h-full">
              <div className="absolute inset-0 overflow-hidden">
                <BlurhashImg
                  src={project.coverImage}
                  placeholderColor="#888"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/fallback-image.jpg';
                  }}
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-white bg-opacity-90">
                <h2 className="text-black text-lg font-semibold truncate">
                  {project.title}
                </h2>
              </div>
            </div>
          </Link>
        </div>
      ))}
    </div>



      {/* Third Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {/* Large Featured Project */}
        <div 
          className="md:col-span-2 lg:col-span-2"
          style={{ height: remainingProjects.length >= 10 ? '320px' : 'auto' }}
        >
          {remainingProjects[7] && (
            <Link to={getProjectLink(remainingProjects[7])} className="block h-full project-card">
              <div className="relative h-full group">
                <div className="h-full overflow-hidden">
                  <BlurhashImg
                    src={remainingProjects[7].coverImage}
                    placeholderColor="#888"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/fallback-image.jpg';
                    }}
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4" style={{ background: "#E9E9E1", opacity: "0.95" }}>
                  <h2 className="text-black text-lg font-semibold truncate">
                    {remainingProjects[7].title}
                  </h2>
                </div>
              </div>
            </Link>
          )}
        </div>

        {/* Smaller Projects */}
        {remainingProjects.slice(8, 10).map((project) => (
          <div 
            key={project.id} 
            className="md:col-span-1" 
            style={{ height: '320px' }}
          >
            <Link to={getProjectLink(project)} className="block h-full project-card">
              <div className="relative h-full">
                <div className="h-full overflow-hidden">
                  <BlurhashImg
                    src={project.coverImage}
                    placeholderColor="#888"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/fallback-image.jpg';
                    }}
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4" style={{ background: "#E9E9E1", opacity: "0.95" }}>
                  <h2 className="text-black text-lg font-semibold truncate">
                    {project.title}
                  </h2>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>


      {/* Remaining Projects Grid */}
      {remainingProjects.slice(10).length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {remainingProjects.slice(10, 10 + visibleRemainingCount).map((project) => (
              <Link key={project.id} to={getProjectLink(project)} className="block h-full project-card" style={{ height: '320px' }}>
                <div className="relative h-full">
                  <div className="h-full overflow-hidden">
                    <BlurhashImg
                      src={project.coverImage}
                      placeholderColor="#888"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Error loading image:', project.coverImage);
                        e.target.src = '/fallback-image.jpg';
                      }}
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4" style={{background: "#E9E9E1", opacity: "0.95"}}>
                    <h2 className="text-black text-lg font-semibold truncate">
                      {project.title}
                    </h2>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Load More Button */}
          {remainingProjects.slice(10).length > visibleRemainingCount && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => setVisibleRemainingCount(prevCount => prevCount + 3)}
                className="px-6 py-8 mt-4 text-red-500 hover:text-red-600 font-semibold transition-colors"
                style={{ fontFamily: "Poppins-SemiBold" }}
              >
                Load More
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Learn;










// import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { collection, getDocs } from 'firebase/firestore';
// import { db } from '../../firebase';
// import BlurhashImg from '../../@/components/Image/BlurhashImg';
// import './learn.css';

// const categories = [
//   { id: 1, name: "Architectural Design", value: "Architecture" },
//   { id: 2, name: "Industrial Design", value: "Industrial" },
//   { id: 3, name: "Graphics Design", value: "Graphics" },
//   { id: 4, name: "Furniture", value: "Furniture" }
// ];

// const Learn = () => {
//   const [projects, setProjects] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedCategory, setSelectedCategory] = useState('all');

//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const projectsCollection = collection(db, 'projects');
//         const snapshot = await getDocs(projectsCollection);
        
//         const fetchedProjects = snapshot.docs.map((doc) => {
//           const data = doc.data();
//           return {
//             id: doc.id,
//             ...data,
//             isVisible: data.isVisible !== undefined ? data.isVisible : true
//           };
//         });

//         fetchedProjects.sort((a, b) => {
//           const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
//           const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
//           return dateB - dateA;
//         });

//         setProjects(fetchedProjects);
//       } catch (error) {
//         console.error("Error fetching projects:", error);
//         setError("Failed to load projects. Please try again later.");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchProjects();
//   }, []);

//   if (loading) return <div>Loading projects...</div>;
//   if (error) return <div>Error: {error}</div>;

//   const visibleProjects = projects.filter((project) => project.isVisible);
//   console.log('Visible projects:', visibleProjects);
//   console.log('Number of visible projects:', visibleProjects.length);


//   const filteredProjects = projects.filter((project) => {
//     // Normalize category matching (case-insensitive)
//     const projectCategory = (project.category || '').toLowerCase().trim();
//     const selectedCategoryLower = selectedCategory.toLowerCase().trim();
    
//     const categoryMatch = 
//       selectedCategory === 'all' || 
//       projectCategory === selectedCategoryLower ||
//       // Add additional mapping for exact matches
//       (selectedCategoryLower === 'architecture' && projectCategory === 'architectural') ||
//       (selectedCategoryLower === 'industrial' && projectCategory === 'industrial') ||
//       (selectedCategoryLower === 'graphics' && projectCategory === 'graphics') ||
//       (selectedCategoryLower === 'furniture' && projectCategory === 'furniture');
    
//     return project.isVisible && categoryMatch;
//   });

//  // Debugging logs
//  console.log('Total Projects:', projects.length);
//  console.log('Selected Category:', selectedCategory);
//  console.log('Filtered Projects:', filteredProjects.length);
//  filteredProjects.forEach(project => {
//    console.log('Project Category:', project.category);
//  });


//   // If no projects match the selected category
//   if (filteredProjects.length === 0) {
//     return (
//       <div className="mx-auto section__paddingda">
//         <div className="category-buttons flex items-center justify-between mb-2">
//           <div className="category-buttona">
//             <button
//               key="all"
//               onClick={() => setSelectedCategory('all')}
//               className={`px-4 pt-2 pb-0 transition-all duration-300 ${
//                 selectedCategory === 'all'
//                   ? 'text-[#CD3333]'
//                   : 'text-black'
//               }`}
//               style={{ fontFamily: "Poppins-Regular", fontSize: '14px' }}
//             >
//               All
//             </button>
//             {categories.map((category) => (
//               <button
//                 key={category.id}
//                 onClick={() => setSelectedCategory(category.value)}
//                 className={`px-4 pt-2 pb-0 transition-all duration-300 ${
//                   selectedCategory === category.value
//                     ? 'text-[#CD3333]'
//                     : 'text-black'
//                 }`}
//                 style={{ fontFamily: "Poppins-Regular", fontSize: '14px' }}
//               >
//                 {category.name}
//               </button>
//             ))}
//           </div>
//         </div>
//         <div className="text-center pt-12 pb-0">
//           <h2 className="text-xl font-semibold mb-2">No projects found</h2>
//           <p>There are currently no projects in the {selectedCategory} category.</p>
//         </div>
//       </div>
//     );
//   }

  
//   const featuredProject = visibleProjects[0];
//   const remainingProjects = visibleProjects.slice(1);

//   const getProjectLink = (project) => {
//     return project.slug ? `/work/${project.slug}` : `/work/${project.id}`;
//   };

//   return (
//     <div className="mx-auto section__paddingda">
//       {/* Category buttons section */}
//       <div className="category-buttons flex items-center justify-between mb-2">
//         <div className="flex items-center gap-4">
//           <div className="flex gap-2">
//             <button
//               key="all"
//               onClick={() => setSelectedCategory('all')}
//               className={`transition-all duration-300 ${
//                 selectedCategory === 'all' 
//                   ? 'text-[#CD3333]'
//                   : 'text-black hover:text-gray-600'
//               } bg-transparent`}
//               style={{ fontFamily: "Poppins-Regular", fontSize: '16px', color: '#000000' }}
//             >
//               All
//             </button>
//             {categories.map((category) => (
//               <button
//                 key={category.id}
//                 onClick={() => setSelectedCategory(category.value)}
//                 className={`transition-all duration-300 ${
//                   selectedCategory === category.value 
//                     ? 'text-[#CD3333]'
//                     : 'text-black hover:text-gray-600'
//                 } bg-transparent`}
//                 style={{ fontFamily: "Poppins-Regular", fontSize: '16px', color: '#000000' }}
//               >
//                 {category.name}
//               </button>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* First Section */}
//       <div className="grid grid-cols-4 auto-rows-[200px] gap-4">
//         {/* Featured Project */}
//         <div className="col-span-2 row-span-2">
//           {featuredProject && (
//             <Link to={getProjectLink(featuredProject)} className="block h-full">
//               <div className="relative h-full group">
//                 <div className="shadow-lg h-full overflow-hidden hover:shadow-xl transition-shadow duration-300">
//                   <BlurhashImg
//                     src={featuredProject.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover"
//                   />
//                 </div>
//                 <div className="absolute bottom-0 left-0 right-0 p-4 bg-transparent">
//                   <h2 className="text-black text-xl font-semibold truncate" style={{ fontFamily: "Poppins-SemiBold", fontSize: '16px' }}>
//                     {featuredProject.title}
//                   </h2>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>

//         {/* Grid of 4 Projects */}
//         {remainingProjects.slice(0, 4).map((project, index) => (
//           <div
//             key={project.id}
//             className={`${
//               index === 0 ? 'col-start-3 row-start-1' : ''
//             } ${
//               index === 1 ? 'col-start-4 row-start-1' : ''
//             } ${
//               index === 2 ? 'col-start-3 row-start-2' : ''
//             } ${
//               index === 3 ? 'col-start-4 row-start-2' : ''
//             }`}
//           >
//             <Link to={getProjectLink(project)} className="block h-full">
//               <div className="relative h-full">
//                 <div className="h-full overflow-hidden">
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="absolute bottom-0 left-0 right-0 p-4 bg-white">
//                   <h2 className="text-black text-lg font-semibold truncate" style={{ fontFamily: "Poppins-SemiBold", fontSize: '16px' }}>
//                     {project.title}
//                   </h2>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>

//       {/* Remaining sections are the same as in the previous implementation */}
//       {/* Second Section */}
//       <div className="grid grid-cols-4 gap-6 mt-6 auto-rows-[240px]">
//         {/* Large Featured Project */}
//         <div className="col-span-2 col-start-3 row-span-2">
//           {remainingProjects[4] && (
//             <Link to={getProjectLink(remainingProjects[4])} className="block h-full">
//               <div className="relative h-full group">
//                 <div className="shadow-lg h-full overflow-hidden hover:shadow-xl transition-shadow duration-300">
//                   <BlurhashImg
//                     src={remainingProjects[4].coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="absolute bottom-0 left-0 right-0 p-4 bg-white">
//                   <h2 className="text-black text-xl font-semibold truncate" style={{ fontFamily: "Poppins-SemiBold", fontSize: '16px' }}>
//                     {remainingProjects[4].title}
//                   </h2>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>

//         {/* Smaller Projects */}
//         {remainingProjects.slice(5, 7).map((project, index) => (
//           <div
//             key={project.id}
//             className={`col-span-2 ${index === 0 ? 'col-start-1 row-start-1' : 'col-start-1 row-start-2'}`}
//           >
//             <Link to={getProjectLink(project)} className="block h-full">
//               <div className="relative h-full">
//                 <div className="shadow-lg h-full overflow-hidden hover:shadow-xl transition-all duration-300">
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="absolute bottom-0 left-0 right-0 p-4 bg-white">
//                   <h2 className="text-black text-lg font-semibold truncate" style={{ fontFamily: "Poppins-SemiBold", fontSize: '16px' }}>
//                     {project.title}
//                   </h2>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>

//       {/* Third Section */}
//       <div className="grid grid-cols-4 gap-6 mt-6 auto-rows-[320px]">
//         {/* Large Featured Project */}
//         <div className="col-span-2">
//           {remainingProjects[7] && (
//             <Link to={getProjectLink(remainingProjects[7])} className="block h-full">
//               <div className="relative h-full group">
//                 <div className="shadow-lg h-full overflow-hidden hover:shadow-xl transition-shadow duration-300">
//                   <BlurhashImg
//                     src={remainingProjects[7].coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="absolute bottom-0 left-0 right-0 p-4 bg-white">
//                   <h2 className="text-black text-xl font-semibold truncate" style={{ fontFamily: "Poppins-SemiBold", fontSize: '16px' }}>
//                     {remainingProjects[7].title}
//                   </h2>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>

//         {/* Smaller Projects */}
//         {remainingProjects.slice(8, 10).map((project) => (
//           <div key={project.id} className="col-span-1">
//             <Link to={getProjectLink(project)} className="block h-full">
//               <div className="relative h-full">
//                 <div className="shadow-lg h-full overflow-hidden hover:shadow-xl transition-all duration-300">
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="absolute bottom-0 left-0 right-0 p-4 bg-white">
//                   <h2 className="text-black text-lg font-semibold truncate" style={{ fontFamily: "Poppins-SemiBold", fontSize: '16px' }}>
//                     {project.title}
//                   </h2>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>

//       {/* Remaining Projects Grid */}
//       <div className="grid grid-cols-3 gap-8 mt-6">
//         {remainingProjects.slice(10).map((project) => (
//           <Link key={project.id} to={getProjectLink(project)} className="block h-full">
//             <div className="relative h-full">
//               <div className="shadow-lg h-full overflow-hidden hover:shadow-xl transition-shadow duration-300">
//                 <BlurhashImg
//                   src={project.coverImage}
//                   placeholderColor="#888"
//                   className="w-full h-full object-cover"
//                   onError={(e) => {
//                     console.error('Error loading image:', project.coverImage);
//                     e.target.src = '/fallback-image.jpg';
//                   }}
//                 />
//               </div>
//               <div className="absolute bottom-0 left-0 right-0 p-4 bg-white">
//                 <h2 className="text-black text-lg font-semibold truncate" style={{ fontFamily: "Poppins-SemiBold", fontSize: '16px' }}>
//                   {project.title}
//                 </h2>
               
//               </div>
//             </div>
//           </Link>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default Learn;









// import React from 'react';
// import './learn.css';
// import { Post } from '../../@/components';
// import { useParams, useNavigate } from 'react-router-dom';

// const Learn = ({ articles }) => {
//   const { articleSlug } = useParams();
//   const navigate = useNavigate();

//   const handleReadMore = (article) => {
//     navigate(`/ideas/${article.slug}`);
//   };

//   const handleCloseModal = () => {
//     navigate('/ideas');
//   };

//   return (
//     <div className="learn" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
//       <Post 
//         articles={articles} 
//         onReadMore={handleReadMore} 
//         selectedArticle={articles.find(a => a.slug === articleSlug)}
//         onCloseModal={handleCloseModal}
//       />
//       <div className="ulev__learn section__padding" id=""></div>
//     </div>
//   );
// };

// export default Learn;











// import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { collection, getDocs } from 'firebase/firestore';
// import { db } from '../../firebase';
// import BlurhashImg from '../../@/components/Image/BlurhashImg';
// import './learn.css';

// const Learn = () => {
//   const [projects, setProjects] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const projectsCollection = collection(db, 'projects');
//         const snapshot = await getDocs(projectsCollection);
//         const fetchedProjects = snapshot.docs.map((doc) => {
//           const data = doc.data();
//           return { id: doc.id, ...data };
//         });

//         fetchedProjects.sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0));
//         setProjects(fetchedProjects);
//       } catch (error) {
//         console.error("Error fetching projects:", error);
//         setError("Failed to load projects. Please try again later.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProjects();
//   }, []);

//   if (loading) return <div>Loading projects...</div>;
//   if (error) return <div>Error: {error}</div>;

//   const visibleProjects = projects.filter((project) => project.isVisible);
//   const featuredProject = visibleProjects[0];
//   const remainingProjects = visibleProjects.slice(1);

//   const getProjectLink = (project) => {
//     return project.slug ? `/work/${project.slug}` : `/work/${project.id}`;
//   };

//   return (
//     <div className="mx-auto section__paddingda">
//       <div className="category-buttons">
//         <h1 style={{ fontFamily: "Poppins-SemiBold", color: "#CD3333", fontSize: "37px", fontWeight: "600", lineHeight: "1.1" }}>
//           Explore <br /> Our Projects
//         </h1>
//         <button className='btn2' style={{ marginLeft: 'auto', marginTop: 'auto' }}>
//           <Link to="/work" style={{ fontFamily: "Poppins-Light", color: '#000000' }}>
//             View all Projects
//           </Link>
//         </button>
//       </div>

//       {/* First Section */}
//       <div className="grid grid-cols-4 auto-rows-[200px] gap-4">
//         {/* Featured Project */}
//         <div className="col-span-2 row-span-2">
//           {featuredProject && (
//             <Link to={getProjectLink(featuredProject)} className="block h-full">
//               <div className="bg-white shadow-lg h-full overflow-hidden group hover:shadow-xl transition-shadow duration-300">
//                 <div className="relative h-3/4">
//                   <BlurhashImg
//                     src={featuredProject.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover"
//                   />
//                 </div>
//                 <div className="p-4">
//                   <h2 className="text-black text-xl font-semibold truncate">
//                     {featuredProject.title}
//                   </h2>
//                   <p className="text-black text-sm mt-1 line-clamp-2">
//                     {featuredProject.description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>

//         {/* Grid of 4 Projects */}
//         {remainingProjects.slice(0, 4).map((project, index) => (
//           <div
//             key={project.id}
//             className={`${
//               index === 1 ? 'col-start-3 row-span-2' : ''
//             } ${
//               index === 2 ? 'col-start-4 row-start-1' : ''
//             } ${
//               index === 3 ? 'col-start-4 row-start-2' : ''
//             }`}
//           >
//             <Link to={getProjectLink(project)} className="block h-full">
//               <div 
//                 className={`bg-white shadow-lg h-full overflow-hidden 
//                 hover:shadow-xl transition-all duration-300 
//                 ${index === 1 ? 'flex flex-col' : ''}`}
//               >
//                 <div 
//                   className={`relative ${
//                     index === 1 ? 'flex-1 min-h-[60%]' : 'h-[140px]'
//                   }`}
//                 >
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover rounded-t-lg"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="p-4 flex flex-col justify-between flex-1">
//                   <div>
//                     <h2 className="text-black font-semibold truncate">
//                       {project.title || 'Untitled Project'}
//                     </h2>
//                     <p className="text-black text-sm mt-2 line-clamp-2 text-gray-600">
//                       {project.description || 'No description available.'}
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>

//       {/* Second Section */}
//       <div className="grid grid-cols-4 gap-6 mt-6 auto-rows-[240px]">
//         {/* Large Featured Project */}
//         <div className="col-span-2 col-start-3 row-span-2">
//           {remainingProjects[4] && (
//             <Link to={getProjectLink(remainingProjects[4])} className="block h-full">
//               <div className="bg-white shadow-lg h-full overflow-hidden group hover:shadow-xl transition-shadow duration-300">
//                 <div className="relative h-3/4">
//                   <BlurhashImg
//                     src={remainingProjects[4].coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover rounded-t-lg"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="p-4">
//                   <h2 className="text-black text-xl font-semibold truncate">
//                     {remainingProjects[4].title}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-1 line-clamp-2">
//                     {remainingProjects[4].description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>

//         {/* Smaller Projects Spanning Entire Column Width */}
//         {remainingProjects.slice(5, 7).map((project, index) => (
//           <div
//             key={project.id}
//             className={`col-span-2 ${index === 0 ? 'col-start-1 row-start-1' : 'col-start-1 row-start-2'}`}
//           >
//             <Link to={getProjectLink(project)} className="block h-full">
//               <div className="bg-white shadow-lg h-full overflow-hidden hover:shadow-xl transition-all duration-300">
//                 <div className="relative h-[160px] md:h-[200px] lg:h-[240px]">
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover rounded-t-lg"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="p-4 flex flex-col justify-between flex-1">
//                   <h2 className="text-black text-lg font-semibold truncate">
//                     {project.title || 'Untitled Project'}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-2 line-clamp-2">
//                     {project.description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>

//       {/* Second Section */}
//       <div className="grid grid-cols-4 gap-6 mt-6 auto-rows-[320px]"> {/* Increased row height */}
//         {/* Large Featured Project */}
//         <div className="col-span-2">
//           {remainingProjects[4] && (
//             <Link to={getProjectLink(remainingProjects[4])} className="block h-full">
//               <div className="bg-white shadow-lg h-full overflow-hidden group hover:shadow-xl transition-shadow duration-300">
//                 <div className="relative h-3/4">
//                   <BlurhashImg
//                     src={remainingProjects[4].coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover rounded-t-lg"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="p-4">
//                   <h2 className="text-black text-xl font-semibold truncate">
//                     {remainingProjects[4].title}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-1 line-clamp-2">
//                     {remainingProjects[4].description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>

//         {/* Smaller Projects Spanning Entire Column Width */}
//         {remainingProjects.slice(5, 7).map((project, index) => (
//           <div key={project.id} className="col-span-1">
//             <Link to={getProjectLink(project)} className="block h-full">
//               <div className="bg-white shadow-lg h-full overflow-hidden hover:shadow-xl transition-all duration-300">
//                 <div className="relative h-[180px] md:h-[220px] lg:h-[260px]"> {/* Increased height here */}
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover rounded-t-lg"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="p-4 flex flex-col justify-between flex-1">
//                   <h2 className="text-black text-lg font-semibold truncate">
//                     {project.title || 'Untitled Project'}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-2 line-clamp-2">
//                     {project.description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>
      
//       <div className="work-container grid grid-cols-3 gap-8 mt-6">
//         {remainingProjects.slice(12).map((project) => (
//           <Link key={project.id} to={getProjectLink(project)} className="block h-full">
//             <div className="bg-white shadow-lg h-full overflow-hidden group hover:shadow-xl transition-shadow duration-300">
//               <div className="relative h-[160px] md:h-[200px] lg:h-[240px]">
//                 <BlurhashImg
//                   src={project.coverImage}
//                   placeholderColor="#888"
//                   className="w-full h-full object-cover rounded-t-lg"
//                   onError={(e) => {
//                     console.error('Error loading image:', project.coverImage);
//                     e.target.src = '/fallback-image.jpg';
//                   }}
//                 />
//               </div>
//               <div className="p-4 flex flex-col justify-between flex-1">
//                 <h2 className="text-black text-lg font-semibold truncate">
//                   {project.title || 'Untitled Project'}
//                 </h2>
//                 <p className="text-gray-600 text-sm mt-2 line-clamp-2">
//                   {project.description || 'No description available.'}
//                 </p>
//               </div>
//             </div>
//           </Link>
//         ))}
//       </div>

//     </div>
//   );
// };

// export default Learn;








// import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { collection, getDocs } from 'firebase/firestore';
// import { db } from '../../firebase';
// import BlurhashImg from '../../@/components/Image/BlurhashImg';
// import './learn.css';

// const categories = [
//   { id: 1, name: "Architectural Design", value: "Graphics" },
//   { id: 2, name: "Commercial", value: "Commercial" },
//   { id: 3, name: "Industrial", value: "Industrial" },
//   { id: 4, name: "Interior", value: "Interior" }
// ];

// const Learn = () => {
//   const [projects, setProjects] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedCategory, setSelectedCategory] = useState('all');

//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const projectsCollection = collection(db, 'projects');
//         const snapshot = await getDocs(projectsCollection);
        
//         const fetchedProjects = snapshot.docs.map((doc) => {
//           const data = doc.data();
//           return {
//             id: doc.id,
//             ...data,
//             isVisible: data.isVisible !== undefined ? data.isVisible : true
//           };
//         });

//         fetchedProjects.sort((a, b) => {
//           const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
//           const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
//           return dateB - dateA;
//         });

//         setProjects(fetchedProjects);
//       } catch (error) {
//         console.error("Error fetching projects:", error);
//         setError("Failed to load projects. Please try again later.");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchProjects();
//   }, []);

//   if (loading) return <div>Loading projects...</div>;
//   if (error) return <div>Error: {error}</div>;

//   const visibleProjects = projects.filter((project) => project.isVisible);
//   console.log('Visible projects:', visibleProjects);
//   console.log('Number of visible projects:', visibleProjects.length);

//   const featuredProject = visibleProjects[0];
//   const remainingProjects = visibleProjects.slice(1);

//   const getProjectLink = (project) => {
//     return project.slug ? `/work/${project.slug}` : `/work/${project.id}`;
//   };

//   const filteredProjects = projects.filter((project) => {
//     const categoryMatch = selectedCategory === 'all' || project.category === selectedCategory;
//     return project.isVisible && categoryMatch;
//   });

//   if (filteredProjects.length === 0) {
//     return (
//       <div className="mx-auto section__paddingda">
//         <div className="category-buttons flex items-center justify-between mb-8">
//           <div className="category-buttona">
//             {categories.map((category) => (
//               <button
//                 key={category.id}
//                 onClick={() => setSelectedCategory(category.value)}
//                 className={`px-4 py-2 rounded-lg transition-all duration-300 ${
//                   selectedCategory === category.value 
//                     // ? 'bg-[#CD3333] text-white'
//                     // : 'bg-gray-100 hover:bg-gray-200 text-black'
//                 }`}
//                 style={{ fontFamily: "Poppins-Regular" }}
//               >
//                 {category.name}
//               </button>
//             ))}
//           </div>
//         </div>
//         <div className="text-center py-12">
//           <h2 className="text-xl font-semibold mb-2">No projects found</h2>
//           <p>There are currently no projects in the {selectedCategory} category.</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="mx-auto section__paddingda">
//       <div className="category-buttons flex items-center justify-between mb-8">
//         <div className="flex items-center gap-4">
//           <div className="flex gap-4">
//             {categories.map((category) => (
//               <button
//                 key={category.id}
//                 onClick={() => setSelectedCategory(category.value)}
//                 className={`px-4 py-2 rounded-lg transition-all duration-300 ${
//                   selectedCategory === category.value 
//                     // ? 'bg-[#CD3333] text-white'
//                     // : 'bg-gray-100 hover:bg-gray-200 text-black'
//                 }`}
//                 style={{ fontFamily: "Poppins-Regular" }}
//               >
//                 {category.name}
//               </button>
//             ))}
//           </div>
//         </div>
//       </div>
//       {/* First Section */}
//       <div className="grid grid-cols-4 auto-rows-[200px] gap-4">
//         {/* Featured Project */}
//         <div className="col-span-2 row-span-2">
//           {featuredProject && (
//             <Link to={getProjectLink(featuredProject)} className="block h-full">
//               <div className="bg-white shadow-lg h-full overflow-hidden group hover:shadow-xl transition-shadow duration-300">
//                 <div className="relative h-3/4">
//                   <BlurhashImg
//                     src={featuredProject.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover"
//                   />
//                 </div>
//                 <div className="p-4">
//                   <h2 className="text-black text-xl font-semibold truncate">
//                     {featuredProject.title}
//                   </h2>
//                   <p className="text-black text-sm mt-1 line-clamp-2">
//                     {featuredProject.description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>
//         {/* Grid of 4 Projects */}
//         {remainingProjects.slice(0, 4).map((project, index) => (
//           <div
//             key={project.id}
//             className={`${
//               index === 1 ? 'col-start-3 row-span-2' : ''
//             } ${
//               index === 2 ? 'col-start-4 row-start-1' : ''
//             } ${
//               index === 3 ? 'col-start-4 row-start-2' : ''
//             }`}
//           >
//             <Link to={getProjectLink(project)} className="block h-full">
//               <div 
//                 className={`bg-white shadow-lg h-full overflow-hidden
//                 hover:shadow-xl transition-all duration-300
//                 ${index === 1 ? 'flex flex-col' : ''}`}
//               >
//                 <div 
//                   className={`relative ${
//                     index === 1 ? 'flex-1 min-h-[60%]' : 'h-[140px]'
//                   }`}
//                 >
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover rounded-t-lg"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="p-4 flex flex-col justify-between flex-1">
//                   <div>
//                     <h2 className="text-black font-semibold truncate">
//                       {project.title || 'Untitled Project'}
//                     </h2>
//                     <p className="text-black text-sm mt-2 line-clamp-2 text-gray-600">
//                       {project.description || 'No description available.'}
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>
//       {/* Second Section */}
//       <div className="grid grid-cols-4 gap-6 mt-6 auto-rows-[240px]">
//         {/* Large Featured Project */}
//         <div className="col-span-2 col-start-3 row-span-2">
//           {remainingProjects[4] && (
//             <Link to={getProjectLink(remainingProjects[4])} className="block h-full">
//               <div className="bg-white shadow-lg h-full overflow-hidden group hover:shadow-xl transition-shadow duration-300">
//                 <div className="relative h-3/4">
//                   <BlurhashImg
//                     src={remainingProjects[4].coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover rounded-t-lg"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="p-4">
//                   <h2 className="text-black text-xl font-semibold truncate">
//                     {remainingProjects[4].title}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-1 line-clamp-2">
//                     {remainingProjects[4].description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>
//         {/* Smaller Projects Spanning Entire Column Width */}
//         {remainingProjects.slice(5, 7).map((project, index) => (
//           <div
//             key={project.id}
//             className={`col-span-2 ${index === 0 ? 'col-start-1 row-start-1' : 'col-start-1 row-start-2'}`}
//           >
//             <Link to={getProjectLink(project)} className="block h-full">
//               <div className="bg-white shadow-lg h-full overflow-hidden hover:shadow-xl transition-all duration-300">
//                 <div className="relative h-[160px] md:h-[200px] lg:h-[240px]">
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover rounded-t-lg"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="p-4 flex flex-col justify-between flex-1">
//                   <h2 className="text-black text-lg font-semibold truncate">
//                     {project.title || 'Untitled Project'}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-2 line-clamp-2">
//                     {project.description || 'Description not specified'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>
//       {/* Third Section */}
//       <div className="grid grid-cols-4 gap-6 mt-6 auto-rows-[320px]">
//         {/* Large Featured Project */}
//         <div className="col-span-2">
//           {remainingProjects[7] && (
//             <Link to={getProjectLink(remainingProjects[7])} className="block h-full">
//               <div className="bg-white shadow-lg h-full overflow-hidden group hover:shadow-xl transition-shadow duration-300">
//                 <div className="relative h-3/4">
//                   <BlurhashImg
//                     src={remainingProjects[7].coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover rounded-t-lg"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="p-4">
//                   <h2 className="text-black text-xl font-semibold truncate">
//                     {remainingProjects[7].title}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-1 line-clamp-2">
//                     {remainingProjects[7].description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>
//         {/* Smaller Projects */}
//         {remainingProjects.slice(8, 10).map((project, index) => (
//           <div key={project.id} className="col-span-1">
//             <Link to={getProjectLink(project)} className="block h-full">
//               <div className="bg-white shadow-lg h-full overflow-hidden hover:shadow-xl transition-all duration-300">
//                 <div className="relative h-[180px] md:h-[220px] lg:h-[260px]">
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-full object-cover rounded-t-lg"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="p-4 flex flex-col justify-between flex-1">
//                   <h2 className="text-black text-lg font-semibold truncate">
//                     {project.title || 'Untitled Project'}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-2 line-clamp-2">
//                     {project.description || 'Description not specified'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>
      
//       <div className="work-container grid grid-cols-3 gap-8 mt-6">
//         {remainingProjects.slice(10).map((project) => (
//           <Link key={project.id} to={getProjectLink(project)} className="block h-full">
//             <div className="bg-white shadow-lg h-full overflow-hidden group hover:shadow-xl transition-shadow duration-300">
//               <div className="relative h-[160px] md:h-[200px] lg:h-[240px]">
//                 <BlurhashImg
//                   src={project.coverImage}
//                   placeholderColor="#888"
//                   className="w-full h-full object-cover rounded-t-lg"
//                   onError={(e) => {
//                     console.error('Error loading image:', project.coverImage);
//                     e.target.src = '/fallback-image.jpg';
//                   }}
// />
//               </div>
//               <div className="p-4 flex flex-col justify-between flex-1">
//                 <h2 className="text-black text-lg font-semibold truncate">
//                   {project.title || 'Untitled Project'}
//                 </h2>
//                 <p className="text-gray-600 text-sm mt-2 line-clamp-2">
//                   {project.description || 'No description available.'}
//                 </p>
//               </div>
//             </div>
//           </Link>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default Learn;




















// import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { collection, getDocs } from 'firebase/firestore';
// import { db } from '../../firebase';
// import BlurhashImg from '../../@/components/Image/BlurhashImg';
// import './learn.css';

// const categories = [
//   { id: 1, name: "Architectural Design", value: "Architecture" },
//   { id: 2, name: "Industrial Design", value: "Industrial" },
//   { id: 3, name: "Graphics Design", value: "Graphics" },
//   { id: 4, name: "Furniture", value: "Furniture" }
// ];

// const Learn = () => {
//   const [projects, setProjects] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedCategory, setSelectedCategory] = useState('all');

//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const projectsCollection = collection(db, 'projects');
//         const snapshot = await getDocs(projectsCollection);
        
//         const fetchedProjects = snapshot.docs.map((doc) => {
//           const data = doc.data();
//           return {
//             id: doc.id,
//             ...data,
//             isVisible: data.isVisible !== undefined ? data.isVisible : true
//           };
//         });

//         fetchedProjects.sort((a, b) => {
//           const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
//           const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
//           return dateB - dateA;
//         });

//         setProjects(fetchedProjects);
//       } catch (error) {
//         console.error("Error fetching projects:", error);
//         setError("Failed to load projects. Please try again later.");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchProjects();
//   }, []);

//   if (loading) return <div>Loading projects...</div>;
//   if (error) return <div>Error: {error}</div>;

//   const visibleProjects = projects.filter((project) => project.isVisible);
//   console.log('Visible projects:', visibleProjects);
//   console.log('Number of visible projects:', visibleProjects.length);

//   const featuredProject = visibleProjects[0];
//   const remainingProjects = visibleProjects.slice(1);

//   const getProjectLink = (project) => {
//     return project.slug ? `/work/${project.slug}` : `/work/${project.id}`;
//   };

//   const filteredProjects = projects.filter((project) => {
//     const categoryMatch = selectedCategory === 'all' || project.category === selectedCategory;
//     return project.isVisible && categoryMatch;
//   });

//   if (filteredProjects.length === 0) {
//     return (
//       <div className="mx-auto section__paddingda">
//         <div className="category-buttons flex items-center justify-between mb-8">
//           <div className="category-buttona">
//             {categories.map((category) => (
//               <button
//                 key={category.id}
//                 onClick={() => setSelectedCategory(category.value)}
//                 className={`px-4 py-2 rounded-lg transition-all duration-300 ${
//                   selectedCategory === category.value 
//                     // ? 'bg-[#CD3333] text-white'
//                     // : 'bg-gray-100 hover:bg-gray-200 text-black'
//                 }`}
//                 style={{ fontFamily: "Poppins-Regular" }}
//               >
//                 {category.name}
//               </button>
//             ))}
//           </div>
//         </div>
//         <div className="text-center py-12">
//           <h2 className="text-xl font-semibold mb-2">No projects found</h2>
//           <p>There are currently no projects in the {selectedCategory} category.</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="mx-auto section__paddingda">
//       {/* Category buttons section - unchanged */}
//       <div className="category-buttons flex items-center justify-between mb-8">
//         <div className="flex items-center gap-4">
//           <div className="flex gap-2">
//             {categories.map((category) => (
//               <button
//                 key={category.id}
//                 onClick={() => setSelectedCategory(category.value)}
//                 className={`transition-all duration-300 ${
//                   selectedCategory === category.value 
//                     ? 'text-[#CD3333]'
//                     : 'text-black hover:text-gray-600'
//                 } bg-transparent`}
//                 style={{ fontFamily: "Poppins-Regular" }}
//               >
//                 {category.name}
//               </button>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* First Section */}
//       <div className="grid grid-cols-4 gap-4">
//         {/* Featured Project */}
//         <div className="col-span-2 row-span-2">
//           {featuredProject && (
//             <Link to={getProjectLink(featuredProject)} className="block">
//               <div className="flex flex-col h-full">
//                 <div className="shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 mb-4">
//                   <BlurhashImg
//                     src={featuredProject.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-[400px] object-cover"
//                   />
//                 </div>
//                 <div className="flex-grow">
//                   <h2 className="text-black text-xl font-semibold truncate">
//                     {featuredProject.title}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-2">
//                     {featuredProject.description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>

//         {/* Grid of 4 Projects */}
//         {remainingProjects.slice(0, 4).map((project, index) => (
//           <div
//             key={project.id}
//             className={`${
//               index === 0 ? 'col-start-3 row-start-1' : ''
//             } ${
//               index === 1 ? 'col-start-4 row-start-1' : ''
//             } ${
//               index === 2 ? 'col-start-3 row-start-2' : ''
//             } ${
//               index === 3 ? 'col-start-4 row-start-2' : ''
//             }`}
//           >
//             <Link to={getProjectLink(project)} className="block h-full">
//               <div className="flex flex-col h-full">
//                 <div className="shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 mb-3">
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-[180px] object-cover"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div className="flex-grow">
//                   <h2 className="text-black text-lg font-semibold truncate">
//                     {project.title}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-1 line-clamp-2">
//                     {project.description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>

//       {/* Second Section */}
//       <div className="grid grid-cols-4 gap-6 mt-12">
//         {/* Large Featured Project */}
//         <div className="col-span-2 col-start-3">
//           {remainingProjects[4] && (
//             <Link to={getProjectLink(remainingProjects[4])} className="block">
//               <div className="flex flex-col">
//                 <div className="shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 mb-4">
//                   <BlurhashImg
//                     src={remainingProjects[4].coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-[400px] object-cover"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div>
//                   <h2 className="text-black text-xl font-semibold truncate">
//                     {remainingProjects[4].title}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-2">
//                     {remainingProjects[4].description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>

//         {/* Smaller Projects */}
//         {remainingProjects.slice(5, 7).map((project, index) => (
//           <div
//             key={project.id}
//             className={`col-span-2 ${index === 0 ? 'col-start-1 row-start-1' : 'col-start-1 row-start-2'}`}
//           >
//             <Link to={getProjectLink(project)} className="block">
//               <div className="flex flex-col">
//                 <div className="shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 mb-4">
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-[240px] object-cover"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div>
//                   <h2 className="text-black text-lg font-semibold truncate">
//                     {project.title}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-2">
//                     {project.description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>

//       {/* Third Section */}
//       <div className="grid grid-cols-4 gap-6 mt-12">
//         {/* Large Featured Project */}
//         <div className="col-span-2">
//           {remainingProjects[7] && (
//             <Link to={getProjectLink(remainingProjects[7])} className="block">
//               <div className="flex flex-col">
//                 <div className="shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 mb-4">
//                   <BlurhashImg
//                     src={remainingProjects[7].coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-[320px] object-cover"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div>
//                   <h2 className="text-black text-xl font-semibold truncate">
//                     {remainingProjects[7].title}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-2">
//                     {remainingProjects[7].description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           )}
//         </div>

//         {/* Smaller Projects */}
//         {remainingProjects.slice(8, 10).map((project) => (
//           <div key={project.id} className="col-span-1">
//             <Link to={getProjectLink(project)} className="block">
//               <div className="flex flex-col">
//                 <div className="shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 mb-3">
//                   <BlurhashImg
//                     src={project.coverImage}
//                     placeholderColor="#888"
//                     className="w-full h-[260px] object-cover"
//                     onError={(e) => {
//                       e.target.src = '/fallback-image.jpg';
//                     }}
//                   />
//                 </div>
//                 <div>
//                   <h2 className="text-black text-lg font-semibold truncate">
//                     {project.title}
//                   </h2>
//                   <p className="text-gray-600 text-sm mt-1">
//                     {project.description || 'No description available.'}
//                   </p>
//                 </div>
//               </div>
//             </Link>
//           </div>
//         ))}
//       </div>

//       {/* Remaining Projects Grid */}
//       <div className="grid grid-cols-3 gap-8 mt-12">
//         {remainingProjects.slice(10).map((project) => (
//           <Link key={project.id} to={getProjectLink(project)} className="block">
//             <div className="flex flex-col">
//               <div className="shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 mb-4">
//                 <BlurhashImg
//                   src={project.coverImage}
//                   placeholderColor="#888"
//                   className="w-full h-[240px] object-cover"
//                   onError={(e) => {
//                     console.error('Error loading image:', project.coverImage);
//                     e.target.src = '/fallback-image.jpg';
//                   }}
//                 />
//               </div>
//               <div>
//                 <h2 className="text-black text-lg font-semibold truncate">
//                   {project.title}
//                 </h2>
//                 <p className="text-gray-600 text-sm mt-2">
//                   {project.description || 'No description available.'}
//                 </p>
//               </div>
//             </div>
//           </Link>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default Learn;











//       {/* Fourth Section */}
//       {/* <div className="work-container grid grid-cols-3 gap-8 mt-6">
//         {remainingProjects.slice(12).map((project) => (
//           <Link key={project.id} to={getProjectLink(project)} className="block">
//             <div className="project-card overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
//               <BlurhashImg
//                 src={project.coverImage}
//                 placeholderColor="#888"
//                 className="w-full h-[240px] object-cover rounded-lg"
//                 onError={(e) => {
//                   console.error('Error loading image:', project.coverImage);
//                   e.target.src = '/fallback-image.jpg';
//                 }}
//               />
//               <h2 className="text-black text-lg mt-2 font-semibold">
//                 {project.title || 'Untitled Project'}
//               </h2>
//               <p className="text-black text-sm mt-1 font-normal">
//                 {project.description || 'No description available.'}
//               </p>
//             </div>
//           </Link>
//         ))}
//       </div> */}